// © 2026 AttainIP Engineering
/**
 * Which SURFACE an organisation belongs to, for the admin directory only.
 *
 * RFC-0510 F37. `orgChoiceSubLabel` shows the readable half of an `ext_id`, and
 * `readableOrgExtId` strips the prefix to get it. That is right in the
 * organisation PICKER, where a human is choosing between firms and the prefix
 * is noise. It is wrong in the admin directory, where the prefix is the entire
 * distinguishing fact:
 *
 *     enterprise-domain:rothl.com   →  "Rothl" / "rothl.com"
 *     email-domain:rothl.com        →  "Rothl" / "rothl.com"
 *
 * Two DIFFERENT organisations, which under RFC-0160 cannot see each other's
 * data, printed identically. Seen on beta 2026-09-09 in a three-row listing for
 * one human — an enterprise identity, a self-serve one, and a guest.
 *
 * ⚠️ THIS IS NOT INVISIBLE TODAY, AND THE HONEST FRAMING MATTERS. The Type
 * column correlates: a `selfserve` row sits in `email-domain:`, an `enterprise`
 * or `guest` row in `enterprise-domain:`. So an operator who knows the model
 * can reason it out from the next column along. The complaint is narrower than
 * "an admin cannot tell": a fact you must INFER from a neighbouring column
 * should be STATED by the column that claims to show it.
 *
 * Kept out of `orgChoiceLabel` deliberately. A firm must read identically in
 * the directory and in the picker, so the shared helpers do not move; this
 * qualifies the sub-line the directory already renders, and nothing else.
 */

/** Identity's `OrgType` values that name a SURFACE rather than a shape. */
const SURFACE_BY_ORG_TYPE: Record<string, string> = {
  // RFC-0500 org-per-firm: the enterprise surface's organisation for a domain.
  enterprise_domain: 'enterprise',
  // The self-serve surface's organisation for the same domain — a different
  // row, a different tenancy, and the pair this exists to tell apart.
  domain: 'self-serve',
};

/**
 * The surface qualifier for an organisation type, or `null` when it adds
 * nothing.
 *
 * `null` for `personal` (it has its own label already and holds one human), for
 * `entra_tenant` (a legacy keying that names no surface — see F30/F31), and for
 * an absent or unrecognised value, which is NOT KNOWN rather than a kind
 * (RFC-0510 W29). Saying nothing is right in all three: a qualifier invented
 * from an unknown type would be a confident wrong answer, which is exactly the
 * failure this finding is about.
 */
export function orgSurfaceQualifier(orgType?: string | null): string | null {
  if (!orgType) return null;
  return SURFACE_BY_ORG_TYPE[orgType] ?? null;
}
